import type { TRPCOutputs } from '~/utils/trpc'

type Installation = TRPCOutputs['ops']['stationInstallations']['list'][number]

export const MILESTONES = [
  'created',
  'kickoff',
  'imageReceived',
  'orderPlaced',
  'loggerShipped',
  'instructionSent',
  'installed',
  'indexAligned'
] as const

export type Milestone = (typeof MILESTONES)[number]

export const milestoneLabel: Record<Milestone, string> = {
  created: 'Created',
  kickoff: 'Kickoff',
  imageReceived: 'Image',
  orderPlaced: 'Ordered',
  loggerShipped: 'Shipped',
  instructionSent: 'Instruction',
  installed: 'Installed',
  indexAligned: 'Index aligned'
}

const milestoneDates = (row: Installation): Record<Milestone, string | null> => ({
  created: row.created_at,
  kickoff: row.kickoff_date,
  imageReceived: row.pre_installation_image_added_at,
  orderPlaced: row.order_placed_date,
  loggerShipped: row.logger_shipping_date,
  instructionSent: row.instruction_sent_date,
  installed: row.installation_date,
  indexAligned: row.index_aligned_date
})

const DAY_MS = 24 * 60 * 60 * 1000

// UTC day index, so date-only columns and timestamptz land on one scale and a difference
// can't be knocked off by one across a DST shift the way local-midnight math can.
const utcDayIndex = (value: string): number | null => {
  const time = new Date(value).getTime()
  return Number.isNaN(time) ? null : Math.floor(time / DAY_MS)
}

const daysBetween = (from: string | null, to: string | null): number | null => {
  if (!from || !to) return null
  const start = utcDayIndex(from)
  const end = utcDayIndex(to)
  return start === null || end === null ? null : end - start
}

export interface InstallationTiming {
  installationId: number
  dates: Record<Milestone, string | null>
  plannedDate: string | null
  // Days between the planned and the actual installation date; negative means early.
  slipDays: number | null
  // Planned date already passed with nothing installed. Kept apart from slipDays so a job
  // that never happened counts against the org rather than dropping out of the stats.
  overdueDays: number | null
}

const computeTiming = (row: Installation, now: Date = new Date()): InstallationTiming => {
  const dates = milestoneDates(row)
  const plannedDate = row.planned_installation_date
  const elapsedSincePlanned = daysBetween(plannedDate, now.toISOString())
  return {
    installationId: row.installation_id,
    dates,
    plannedDate,
    slipDays: daysBetween(plannedDate, dates.installed),
    overdueDays:
      dates.installed === null && elapsedSincePlanned !== null && elapsedSincePlanned > 0
        ? elapsedSincePlanned
        : null
  }
}

export interface TimingRow {
  installation: Installation
  timing: InstallationTiming
}

// Paired at the point of computation so no consumer has to re-join a row to its timing.
export const computeTimingRows = (installations: Installation[], now: Date = new Date()): TimingRow[] =>
  installations.map((installation) => ({ installation, timing: computeTiming(installation, now) }))

// Days spent reaching each milestone, measured from the previous milestone the installation
// actually reached — skipped steps fold into the next one rather than reporting zero.
const stepDurations = (timing: InstallationTiming): Partial<Record<Milestone, number>> =>
  MILESTONES.reduce<{ last: string | null; steps: Partial<Record<Milestone, number>> }>(
    (acc, milestone) => {
      const date = timing.dates[milestone]
      if (!date) return acc
      const duration = daysBetween(acc.last, date)
      return {
        last: date,
        steps: duration === null ? acc.steps : { ...acc.steps, [milestone]: duration }
      }
    },
    { last: null, steps: {} }
  ).steps

const median = (values: number[]): number | null => {
  if (values.length === 0) return null
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 0 ? Math.round((sorted[mid - 1] + sorted[mid]) / 2) : sorted[mid]
}

export type DeliveryVerdict = 'not_started' | 'on_track' | 'needs_attention' | 'at_risk' | 'complete'

// Share of outstanding work that is blocked. Thresholds are proportional so one stalled
// installation out of two hundred doesn't read the same as one out of four.
const AT_RISK_BLOCKED_SHARE = 0.25
const NEEDS_ATTENTION_BLOCKED_SHARE = 0.1

export interface OrgDeliveryHealth {
  verdict: DeliveryVerdict
  total: number
  installed: number
  installedPercent: number
  outstanding: number
  // Both scoped to outstanding work: an installation that already shipped is not a delivery
  // risk, whatever its stall record still says.
  stalledCount: number
  overdueCount: number
  // Outstanding installations that are stalled or past their planned date. A row that is both
  // counts once.
  blockedCount: number
  blockedShare: number
  medianCycleDays: number | null
  oldestOpenAgeDays: number | null
  medianOpenAgeDays: number | null
  onTimeCount: number
  lateCount: number
  onTimeRate: number | null
  medianSlipDays: number | null
  medianStepDays: Partial<Record<Milestone, number>>
}

const verdictFor = (total: number, outstanding: number, blockedShare: number): DeliveryVerdict => {
  if (total === 0) return 'not_started'
  if (outstanding === 0) return 'complete'
  if (blockedShare >= AT_RISK_BLOCKED_SHARE) return 'at_risk'
  if (blockedShare >= NEEDS_ATTENTION_BLOCKED_SHARE) return 'needs_attention'
  return 'on_track'
}

export const computeDeliveryHealth = (rows: TimingRow[], now: Date = new Date()): OrgDeliveryHealth => {
  const nowIso = now.toISOString()
  const timings = rows.map((row) => row.timing)
  const allSteps = timings.map(stepDurations)

  const done = rows.filter((row) => row.timing.dates.installed !== null)
  const open = rows.filter((row) => row.timing.dates.installed === null)
  const blocked = open.filter((row) => row.installation.current_stall !== null || row.timing.overdueDays !== null)

  const openAges = open.map((row) => daysBetween(row.timing.dates.created, nowIso)).filter((days) => days !== null)

  const medianStepDays = MILESTONES.reduce<Partial<Record<Milestone, number>>>((acc, milestone) => {
    const value = median(allSteps.map((steps) => steps[milestone]).filter((days) => days !== undefined))
    return value === null ? acc : { ...acc, [milestone]: value }
  }, {})

  const measured = timings.filter((timing) => timing.slipDays !== null)
  const onTimeCount = measured.filter((timing) => (timing.slipDays ?? 0) <= 0).length
  const overdue = timings.filter((timing) => timing.overdueDays !== null)
  // Overdue rows join the denominator: excluding them let one on-time install among fifty
  // no-shows report 100%.
  const ratedCount = measured.length + overdue.length

  const total = rows.length
  const blockedShare = open.length === 0 ? 0 : blocked.length / open.length

  return {
    verdict: verdictFor(total, open.length, blockedShare),
    total,
    installed: done.length,
    installedPercent: total === 0 ? 0 : Math.round((done.length / total) * 100),
    outstanding: open.length,
    stalledCount: open.filter((row) => row.installation.current_stall !== null).length,
    overdueCount: overdue.length,
    blockedCount: blocked.length,
    blockedShare,
    medianCycleDays: median(
      done
        .map((row) => daysBetween(row.timing.dates.created, row.timing.dates.installed))
        .filter((days) => days !== null)
    ),
    oldestOpenAgeDays: openAges.length === 0 ? null : Math.max(...openAges),
    medianOpenAgeDays: median(openAges),
    onTimeCount,
    lateCount: measured.length - onTimeCount,
    onTimeRate: ratedCount === 0 ? null : Math.round((onTimeCount / ratedCount) * 100),
    medianSlipDays: median(measured.map((timing) => timing.slipDays ?? 0)),
    medianStepDays
  }
}
